import React from 'react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetFooter,
  Button,
} from 'lift-ui';
import { X } from 'lucide-react';
import { PantryItem } from '../../api';

interface ViewPantryDialogProps {
  open: boolean;
  onClose: () => void;
  item: PantryItem | null;
}

const ViewPantryDialog: React.FC<ViewPantryDialogProps> = ({ open, onClose, item }) => {
  if (!item) return null;

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  return (
    <Sheet open={open} onOpenChange={onClose}>
      <SheetContent side="right" className="w-[600px] sm:max-w-[600px] flex flex-col">
        <SheetHeader>
          <SheetTitle>Pantry Item Details</SheetTitle>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto py-6 space-y-4">
          {/* ID */}
          <div>
            <p className="text-sm font-medium text-gray-500 mb-1">ID</p>
            <p className="text-base">{item.id}</p>
          </div>

          <div className="border-t border-gray-200" />

          {/* File Name */}
          <div>
            <p className="text-sm font-medium text-gray-500 mb-1">File Name</p>
            <p className="text-base break-words">{item.file_name}</p>
          </div>

          <div className="border-t border-gray-200" />

          {/* Title */}
          <div>
            <p className="text-sm font-medium text-gray-500 mb-1">Title</p>
            <p className="text-base">
              {item.metadata?.title || (
                <span className="italic text-gray-400">No title</span>
              )}
            </p>
          </div>

          <div className="border-t border-gray-200" />

          {/* Description */}
          <div>
            <p className="text-sm font-medium text-gray-500 mb-1">Description</p>
            <p className="text-base whitespace-pre-wrap">
              {item.metadata?.description || (
                <span className="italic text-gray-400">No description</span>
              )}
            </p>
          </div>

          <div className="border-t border-gray-200" />

          {/* Source URL */}
          <div>
            <p className="text-sm font-medium text-gray-500 mb-1">Source URL</p>
            {item.source_url ? (
              <a
                href={item.source_url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-800 underline break-words"
              >
                {item.source_url}
              </a>
            ) : (
              <p className="text-base italic text-gray-400">N/A</p>
            )}
          </div>

          <div className="border-t border-gray-200" />

          {/* Additional Metadata */}
          {item.metadata && Object.keys(item.metadata).filter(k => k !== 'title' && k !== 'description').length > 0 && (
            <>
              <div>
                <p className="text-sm font-medium text-gray-500 mb-1">Additional Metadata</p>
                <div className="bg-gray-100 p-4 rounded-md font-mono text-sm overflow-x-auto">
                  <pre className="m-0">
                    {JSON.stringify(
                      Object.fromEntries(
                        Object.entries(item.metadata).filter(
                          ([key]) => key !== 'title' && key !== 'description'
                        )
                      ),
                      null,
                      2
                    )}
                  </pre>
                </div>
              </div>
              <div className="border-t border-gray-200" />
            </>
          )}

          {/* Timestamps */}
          <div>
            <p className="text-sm font-medium text-gray-500 mb-1">Created At</p>
            <p className="text-sm">{formatDate(item.created_at)}</p>
          </div>

          <div>
            <p className="text-sm font-medium text-gray-500 mb-1">Updated At</p>
            <p className="text-sm">{formatDate(item.updated_at)}</p>
          </div>
        </div>

        <SheetFooter>
          <Button onClick={onClose}>Close</Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
};

export default ViewPantryDialog;
